import React, { useEffect, useState, type MouseEvent, type ReactNode, useContext } from 'react';
import { Box, Button, IconButton, LogoMonogramNavy, Text } from '@stitch-fix/mode-react';
import NextLink from 'next/link';
import { G } from '@mobily/ts-belt';
import { m } from 'framer-motion';
import classNames from 'classnames';
import { NavContextWrapper, NavContext, useHeaderLogic, SimplifiedDropdown } from '@stitch-fix/knit';
import { useFeatureFlags } from '@stitch-fix/sf-next';
import styles from './styles.module.scss';
import { iosAppVersionGreaterThanOrEqualTo, isIosApp, isNonEmptyString } from '../../../utils/helpers';
declare global {
  interface StitchfixGlobal {
    refreshNavData?: () => void;
  }
}
const IOS_APP_MIN_VERSION_FOR_WEB_NAV = '11.6.0';
export type NavProps = {
  title?: string | ReactNode;
  bgColor?: 'white' | 'mint';
  backLinkHref?: string;
  onLogoClick?: () => void;
  onBackClick?: () => void;
  logoCtaLabel?: string;
  hideInIosApp?: boolean;
  /**
   * Content to display on the left side of the nav
   *
   * Note: To display a back button, use `backLinkHref` prop
   */
  leftContent?: React.ReactNode;
  /**
   * Content to display on the right side of the nav
   *
   * default:
   * - Logo icon for unauthenticated client
   * - User drop-down menu for authenticated client
   */
  rightContent?: React.ReactNode;
  variant?: 'standard' | 'subtle';
};
const UserDropDown = ({
  isIosAppView,
  onLogoClick,
  logoCtaLabel = 'Exit'
}: Pick<NavProps, 'onLogoClick' | 'logoCtaLabel'> & {
  isIosAppView: boolean;
}) => {
  const {
    client
  } = useContext(NavContext);
  if (!client) {
    if (!onLogoClick || isIosAppView) {
      return <Box as="span" mr={0.625}>
          <LogoMonogramNavy height="24" width="24" title="Logo" />
        </Box>;
    }
    return <Box as="span">
        <IconButton aria-label={logoCtaLabel} onClick={onLogoClick}>
          <LogoMonogramNavy height="24" width="24" title="Logo" />
        </IconButton>
      </Box>;
  }

  // For iOS app view, we need to intercept the dropdown click to trigger
  // the native menu action instead of showing the web dropdown menu
  const handleIosDropdownClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!isIosAppView) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    window.location.assign('js2ios://helpMenu');
  };
  return <Box display="inline-block" mr={{
    sm: 0.625,
    lg: 0
  }} onClickCapture={handleIosDropdownClick}>
      <Text as="span" setting="body-medium">
        <SimplifiedDropdown client={client} isPrimaryNavVisible={false} isNameVisible={false} />
      </Text>
      <div id="account-dropdown-portal" />
    </Box>;
};
export const Nav = ({
  title,
  bgColor,
  backLinkHref,
  onLogoClick,
  onBackClick,
  logoCtaLabel,
  leftContent,
  rightContent,
  hideInIosApp,
  variant = 'standard'
}: NavProps) => {
  const {
    headerElRef,
    isPrimaryNavVisible,
    headerShellRef,
    setStickiness
  } = useHeaderLogic({
    stickyId: '',
    stickyOffset: 0
  });
  const backButton = isNonEmptyString(backLinkHref) && <Button width="fit" variant="text" as={NextLink} href={backLinkHref} onClick={(e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    onBackClick?.();
  }}
  // If a page prefetches the next page, and a new deploy is made,
  // the props on the next page are empty so we need to disable prefetching
  // Related issue: https://github.com/vercel/next.js/pull/60968
  prefetch={false}>
      Back
    </Button>;
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
    return () => {
      setIsMounted(false);
    };
  }, []);
  const userAgent = isMounted ? window.navigator.userAgent : '';
  const isIosAppView = isIosApp(userAgent);
  const {
    featureFlags
  } = useFeatureFlags('client', 'client.web.use_onboarding_nav_in_ios_app');
  const useWebNavInIosApp = featureFlags?.['client.web.use_onboarding_nav_in_ios_app'] === true && iosAppVersionGreaterThanOrEqualTo(userAgent, IOS_APP_MIN_VERSION_FOR_WEB_NAV);

  // Display the user dropdown if on web or in the iOS app with the feature flag and app version enabled
  const displayUserDropdown = !isIosAppView || useWebNavInIosApp;
  if (isIosAppView && hideInIosApp) {
    return null;
  }
  return <NavContextWrapper rootUrl="">
      <header ref={headerElRef} className={classNames(styles.header, {
      [styles.subtle]: variant === 'subtle'
    })}>
        <div ref={headerShellRef} className={styles.shell}>
          <Box as="nav" bgColor={bgColor === 'mint' ? 'mint-60' : 'white'} className={classNames(styles.nav, {
          [styles.closed]: !isPrimaryNavVisible,
          [styles.bordered]: displayUserDropdown,
          [styles.subtle]: variant === 'subtle'
        })} onTransitionEnd={setStickiness}>
            <Box as="span" className={styles.leftCta}>
              {leftContent ?? backButton}
            </Box>
            <m.span initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.1
          }} className={styles.title}>
              {G.isString(title) ? <Text as="span" setting="body-medium" color="blue-45">
                  {title}
                </Text> : title}
            </m.span>
            {rightContent ?? (displayUserDropdown && <UserDropDown isIosAppView={isIosAppView} logoCtaLabel={logoCtaLabel} onLogoClick={onLogoClick} />)}
          </Box>
        </div>
      </header>
    </NavContextWrapper>;
};